import { useCookie, useRequestURL, type CookieOptions } from 'nuxt/app'
import { computed } from 'vue'

const APP_COOKIE_MAX_AGE = 60 * 60 * 24 * 30
const SECOND_LEVEL_PUBLIC_SUFFIXES = new Set([
  'co.jp',
  'co.kr',
  'co.nz',
  'co.uk',
  'com.au',
  'com.br',
  'com.cn',
  'com.hk',
  'com.sg',
  'net.au',
  'net.cn',
  'org.au',
  'org.cn',
])
type AppCookieValue = string | null | undefined

/** 安全解析对象 Cookie，内容为空或格式错误时返回 null。 */
export const parseCookieJson = <T>(value: string | null | undefined): T | null => {
  if (!value) return null
  try {
    return JSON.parse(value)
  } catch {
    try {
      return JSON.parse(decodeURIComponent(value))
    } catch {
      return null
    }
  }
}

/** 将对象 Cookie 转成可安全写入 Cookie 的字符串。 */
export const stringifyCookieJson = (value: unknown): string | null => {
  if (value === null || value === undefined) return null
  try {
    const serialized = JSON.stringify(value)
    return serialized === undefined ? null : encodeURIComponent(serialized)
  } catch {
    return null
  }
}

/** 根据当前请求 host 解析可跨子域共享的一级域名。 */
export const resolveAppCookieDomain = (host: string): string | undefined => {
  const hostname =
    host
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .split('/')[0]
      ?.replace(/:\d+$/, '') || ''

  if (
    !hostname ||
    hostname === 'localhost' ||
    hostname === '0.0.0.0' ||
    hostname === '127.0.0.1' ||
    hostname.includes(':') ||
    /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname) ||
    hostname.startsWith('192.168.')
  ) {
    return undefined
  }

  const parts = hostname.split('.').filter(Boolean)
  if (parts.length < 2) {
    return undefined
  }

  const publicSuffix = parts.slice(-2).join('.')
  const domainPartCount = SECOND_LEVEL_PUBLIC_SUFFIXES.has(publicSuffix) ? 3 : 2
  return `.${parts.slice(-domainPartCount).join('.')}`
}

/**
 * 应用统一 Cookie 入口：默认跨当前一级域名、根路径保存 30 天，调用方参数具有更高优先级。
 */
export const useAppCookie = (name: string, options: CookieOptions<AppCookieValue> = {}) => {
  const domain = resolveAppCookieDomain(useRequestURL().hostname)

  return useCookie(name, {
    domain,
    path: '/',
    maxAge: APP_COOKIE_MAX_AGE,
    ...options,
    decode: (value: string | null | undefined) => value,
    encode: (value: AppCookieValue) => String(value),
  })
}

/** 对象 Cookie 统一负责 JSON 读写，底层仍使用字符串 Cookie。 */
export const useAppObjectCookie = <T>(
  name: string,
  defaultValue: T | null = null,
  options: CookieOptions<AppCookieValue> = {},
) => {
  const cookie = useAppCookie(name, {
    ...options,
    default: () => stringifyCookieJson(defaultValue),
  })

  return computed<T | null>({
    get: () => parseCookieJson<T>(cookie.value) ?? defaultValue,
    set: (value) => {
      cookie.value = stringifyCookieJson(value)
    },
  })
}

/** 删除一级域名 Cookie，并兼容清理历史 host-only Cookie。 */
export const clearAppCookie = (name: string): void => {
  const domain = resolveAppCookieDomain(useRequestURL().hostname)
  useAppCookie(name).value = null

  if (domain) {
    useAppCookie(name, { domain: undefined }).value = null
  }
}

/** 批量删除指定 Cookie。 */
export const clearAppCookies = (names: readonly string[]): void => {
  names.forEach(clearAppCookie)
}
